/**
 * MY RECORD (E1) — the student's only academic screen, with tabs
 * *Progress · Grades · Attendance · Payments · Certificates*.
 *
 * This used to be a loose list of enrollments/attendance/certificates that
 * only a guardian could reach from the nav: the student had nowhere to see
 * their own data consolidated, even though it had been synced for a while
 * (F3-D). The new tabs assemble that data; the ones that already existed on
 * another screen are LINKED, not copied:
 *
 *  · **Payments** mounts `StudentPayments` (features/finance), the same
 *    screen as `/payments` — there's no second account statement.
 *  · **Certificates** links to `/verify/:id`, the public verification that
 *    already exists.
 *  · **Progress** reuses `CompletionProgress` (the server-authoritative criterion).
 *
 * The guardian's WARD selector still lives here, above the tabs: it's the
 * same screen, viewed for another person.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { BadgeCheck, GraduationCap, Users } from "@fleet/icons";
import { attendanceStatsOf, selectWard } from "@space/shared";
import { Link } from "@tanstack/solid-router";
import { stableRows } from "@/lib/solid/stable-rows";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer, PageHeader } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { PillTabPanels, PillTabs } from "@/components/ui/pill-tabs";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { StudentPayments } from "@/features/finance/components/student-payments";
import { CompletionProgress } from "../../completion/completion-progress";
import { CohortAttendanceRecord } from "../record/cohort-attendance-record";
import { CohortReportCard } from "../record/cohort-report-card";
import {
  useMyGuardianWards,
  useUserCertificates,
  useUserEnrollments,
} from "../../hooks/use-people-data";

type RecordTab = "progress" | "grades" | "attendance" | "payments" | "certificates";

const TABS: { id: RecordTab; label: MessageDescriptor; feature?: string }[] = [
  { id: "progress", label: msg`Progress` },
  { id: "grades", label: msg`Grades` },
  { id: "attendance", label: msg`Attendance` },
  { id: "payments", label: msg`Payments`, feature: "billing.core" },
  { id: "certificates", label: msg`Certificates` },
];

/** Enrollments with a live classroom: these are the ones with grades and attendance to look at. */
const OPEN_STATUSES = new Set(["active", "completed"]);

export const AcademicSelfView: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();

  const enrollments = useUserEnrollments(userId);
  const certificates = useUserCertificates(userId);
  const wards = useMyGuardianWards();
  // FLAGS: with no billing in the academy, the payments tab doesn't exist
  // (same gate as the "My payments" nav item).
  const billing = useFeatureEnabled("billing.core");

  const [tab, setTab] = createSignal<RecordTab>("progress");
  const tabItems = createMemo<readonly { id: RecordTab; label: string }[]>((prev = []) => {
    const hasBilling = billing().enabled;
    const filtered = TABS.filter((td) => td.feature === undefined || hasBilling);
    if (prev.length === filtered.length && prev.every((p, i) => p.id === filtered[i]?.id)) {
      return prev;
    }
    return filtered.map((td) => ({
      id: td.id,
      label: t(td.label),
    }));
  });

  const rows = () => enrollments.data ?? [];
  const openRows = stableRows(
    () => (enrollments.data ?? []).filter((e) => OPEN_STATUSES.has(e.status ?? "") && e.cohortId),
    { ignore: ["raw"] },
  );

  const isGuardian = () => (wards.data ?? []).length > 0;
  const [selectedWard, setSelectedWard] = createSignal<string | null>(null);
  const activeWard = createMemo(() =>
    selectWard(
      (wards.data ?? []).map((w) => ({
        student_user_id: w.student_user_id,
        is_primary: w.is_primary,
      })),
      selectedWard(),
    ),
  );

  // F4: selected ward's record — rows arrive via the my_ward_academics
  // stream (ward's enrollments with denormalized grades/attendance).
  const wardEnrollments = useUserEnrollments(() => activeWard() ?? undefined);

  const hasAttendance = () =>
    rows().some((e) => attendanceStatsOf(e.raw as Record<string, unknown>).hasData);

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5">
        <PageHeader
          title={t(msg`My academic record`)}
          description={t(msg`Your progress, grades, attendance, payments and certificates.`)}
          icon={GraduationCap}
        />

        {/* Ward selector (guardian) — defensive */}
        <Show when={isGuardian()}>
          <div class="rounded-2xl border border-border p-4" data-testid="guardian-wards">
            <div class="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Users class="h-4 w-4 text-primary" /> {t(msg`My wards`)}
            </div>
            <div class="flex flex-wrap gap-2">
              <For each={wards.data ?? []}>
                {(w) => (
                  <button
                    type="button"
                    data-testid="ward-chip"
                    onClick={() => setSelectedWard(w.student_user_id)}
                    class={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      activeWard() === w.student_user_id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {w.relationship ?? t(msg`Ward`)}
                  </button>
                )}
              </For>
            </div>
            {/* F4: ward's record — enrollments, grades and attendance
                (my_ward_academics stream; denormalized attendance F3-A). */}
            <Show when={activeWard()}>
              <div class="mt-3 space-y-2" data-testid="ward-enrollments">
                <Show
                  when={(wardEnrollments.data ?? []).length > 0}
                  fallback={
                    <p class="text-xs text-muted-foreground">
                      {t(msg`No enrollments for this ward yet.`)}
                    </p>
                  }
                >
                  <For each={wardEnrollments.data ?? []}>
                    {(e) => {
                      const stats = attendanceStatsOf(e.raw as Record<string, unknown>);
                      return (
                        <div
                          class="flex items-center justify-between rounded-xl border border-border p-3"
                          data-testid="ward-enrollment-row"
                        >
                          <div>
                            <p class="text-sm font-semibold">{e.cohortName ?? e.cohortId}</p>
                            <p class="text-xs text-muted-foreground">
                              {e.status}
                              <Show when={stats.hasData && stats.rate != null}>
                                {" · "}
                                <span data-testid="ward-attendance-rate">
                                  {t(msg`Attendance`)} {stats.rate}%
                                </span>
                              </Show>
                            </p>
                          </div>
                          <Show
                            when={e.finalGrade != null}
                            fallback={
                              <span class="text-xs text-muted-foreground">
                                {t(msg`In progress`)}
                              </span>
                            }
                          >
                            <span
                              class="rounded-full bg-success/15 px-2 py-0.5 text-sm font-bold text-success"
                              data-testid="ward-final-grade"
                            >
                              {e.finalGrade}
                            </span>
                          </Show>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </Show>
          </div>
        </Show>

        <PillTabs
          tabs={tabItems()}
          value={tab()}
          onChange={setTab}
          testIdPrefix="record-tab"
          label={t(msg`Academic record sections`)}
        />

        <PillTabPanels value={tab()} testIdPrefix="record-tab">
          {/* ── Progress ── */}
          <Show when={tab() === "progress"}>
            <section class="space-y-3">
              <h2 class="font-display text-lg font-bold">{t(msg`Enrollments`)}</h2>
              <Show
                when={rows().length > 0}
                fallback={
                  <EmptyState
                    icon={GraduationCap}
                    title={t(msg`No enrollments yet`)}
                    description={t(msg`When you enroll in a cohort it will show up here.`)}
                  />
                }
              >
                <div class="space-y-2" data-testid="self-enrollments">
                  <For each={rows()}>
                    {(e) => (
                      <div class="space-y-2 rounded-xl border border-border p-3">
                        <div class="flex items-center justify-between">
                          <div>
                            <p class="text-sm font-semibold">{e.cohortName ?? e.cohortId}</p>
                            <p class="text-xs text-muted-foreground">{e.status}</p>
                          </div>
                          <Show
                            when={e.finalGrade != null}
                            fallback={
                              <span class="text-xs text-muted-foreground">
                                {t(msg`In progress`)}
                              </span>
                            }
                          >
                            <span class="rounded-full bg-success/15 px-2 py-0.5 text-sm font-bold text-success">
                              {e.finalGrade}
                            </span>
                          </Show>
                        </div>
                        {/* M1: pass progress (completion rules). */}
                        <Show
                          when={(e.status === "active" || e.status === "completed") && e.cohortId}
                        >
                          {(cid) => (
                            <>
                              <CompletionProgress cohortId={cid()} />
                              <Link
                                to="/classroom/$cohortId"
                                params={{ cohortId: cid() }}
                                class="inline-block text-xs font-semibold text-primary hover:underline"
                                data-testid="self-open-classroom"
                              >
                                {t(msg`Open classroom`)} →
                              </Link>
                            </>
                          )}
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </section>
          </Show>

          {/* ── Grades ── */}
          <Show when={tab() === "grades"}>
            <div class="space-y-3" data-testid="self-grades">
              <Show
                when={openRows().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">
                    {t(msg`You have no cohort with grades yet.`)}
                  </p>
                }
              >
                <For each={openRows()}>
                  {(e) => (
                    <CohortReportCard
                      cohortId={e.cohortId ?? ""}
                      cohortName={e.cohortName ?? e.cohortCode ?? e.cohortId ?? ""}
                    />
                  )}
                </For>
              </Show>
            </div>
          </Show>

          {/* ── Attendance ── */}
          <Show when={tab() === "attendance"}>
            <div class="space-y-3" data-testid="self-attendance">
              <Show
                when={openRows().length > 0 || hasAttendance()}
                fallback={
                  <p class="text-sm text-muted-foreground">
                    {t(msg`Attendance statistics aren't available yet.`)}
                  </p>
                }
              >
                <For each={openRows()}>
                  {(e) => (
                    <CohortAttendanceRecord
                      cohortId={e.cohortId ?? ""}
                      cohortName={e.cohortName ?? e.cohortCode ?? e.cohortId ?? ""}
                    />
                  )}
                </For>
              </Show>
            </div>
          </Show>

          {/* ── Payments: the SAME screen as /payments, not a copy ── */}
          <Show when={tab() === "payments"}>
            <div data-testid="self-payments">
              <StudentPayments />
            </div>
          </Show>

          {/* ── Certificates ── */}
          <Show when={tab() === "certificates"}>
            <section class="space-y-3">
              <h2 class="font-display text-lg font-bold">{t(msg`Certificates`)}</h2>
              <Show
                when={(certificates.data ?? []).length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">{t(msg`No certificates yet.`)}</p>
                }
              >
                <div class="space-y-2" data-testid="self-certificates">
                  <For each={certificates.data ?? []}>
                    {(c) => (
                      <Link
                        to="/verify/$certId"
                        params={{ certId: c.id }}
                        class="flex items-center justify-between rounded-xl border border-border p-3 hover:border-primary/50"
                      >
                        <span class="flex items-center gap-2 text-sm font-semibold">
                          <BadgeCheck class="h-4 w-4 text-success" /> {c.course_title}
                        </span>
                      </Link>
                    )}
                  </For>
                </div>
              </Show>
            </section>
          </Show>
        </PillTabPanels>
      </div>
    </PageContainer>
  );
};
